import type { OnboardingDesignLibraryContentData } from "../../onboarding/OnboardingDesignLibraryContent";

import * as React from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import OnboardingDesignLibraryContent from "../../onboarding/OnboardingDesignLibraryContent";

interface ShopDesignLibraryProps {
  onBack: () => void;
  onContinue?: () => void;
  projectId?: string;
}

export default function ShopDesignLibrary({
  onBack,
  onContinue,
  projectId,
}: ShopDesignLibraryProps) {
  const { closeModal } = useModal();
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const onboardingDesignLibraryContentRef = React.useRef<{
    getData: () => OnboardingDesignLibraryContentData;
  }>(null);

  const [selectedOption] = useLocalStorageState(
    "onboarding-setup-selected-option",
    null,
    {
      schema: z.union([z.literal("new"), z.literal("import"), z.null()]),
    },
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal({ type: "createShopFlowModal" });
    }
  };

  const handleSubmit = async () => {
    if (onboardingDesignLibraryContentRef.current) {
      const data = onboardingDesignLibraryContentRef.current.getData();
      const {
        styleLibrary: styleLibraryToSave,
        selectedTheme,
        extractedData,
        hasCompletedManualExtraction,
        hasValidColors,
        hasValidTypography,
        updateStyleLibrary,
      } = data;

      if (styleLibraryToSave && projectId) {
        const hasColors = hasValidColors(styleLibraryToSave.colors);
        const hasTypography = hasValidTypography(styleLibraryToSave.typography);

        const actualSelectedOption =
          selectedOption ??
          localStorage.getItem("onboarding-setup-selected-option");
        const isThemePresetFlow =
          actualSelectedOption === "new" || actualSelectedOption === null; // null means new shop

        const shouldSave = isThemePresetFlow
          ? Boolean(selectedTheme)
          : hasColors ?? hasTypography;

        if (shouldSave) {
          try {
            await updateStyleLibrary({
              projectId,
              styleLibrary: styleLibraryToSave,
            });
          } catch (error) {
            console.error("Error updating style library:", error);
          }
        }
      }

      if (user) {
        let extractionMethod = "theme";
        if (selectedOption === "import") {
          extractionMethod = "shopify";
        } else if (hasCompletedManualExtraction) {
          extractionMethod = "manual";
        }

        logEvent("onboarding.flow.step.completed", {
          userId: user.email,
          stepId: "design-setup",
          stepType: "design-setup",
          stepData: {
            selectedTheme,
            hasExtractedStyles: Boolean(extractedData),
            hasCompletedManualExtraction,
            extractionMethod,
            styleLibrary: styleLibraryToSave
              ? {
                  hasColors: hasValidColors(styleLibraryToSave.colors),
                  hasTypography: hasValidTypography(
                    styleLibraryToSave.typography,
                  ),
                  extractedFontsCount:
                    extractedData?.styles.uploadedFonts?.length ?? 0,
                  extractedColorsCount:
                    extractedData?.styles.colors?.length ?? 0,
                }
              : null,
          },
        });
      }
    }

    closeModal({ type: "createShopFlowModal" });

    if (projectId) {
      navigate(`/shops/${projectId}/site`);
    } else {
      onContinue?.();
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={handleOpenChange}
      size="custom"
      title=""
      description=""
      isClosable={false}
      layoutClassName="h-[900px] w-[1400px]"
      UNSAFE_className="[&_.header-h2]:hidden [&_.text-muted]:hidden"
      footer={
        <div className="w-full">
          <div className="border-t-[0.5px] border-border -mx-6" />
          <div className="flex justify-between items-center w-full pt-4">
            <div className="text-sm text-muted flex items-center h-10 leading-none -mb-4">
              Step 3 of 3
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="secondary" size="base" onClick={onBack}>
                Back
              </Button>
              <Button
                variant="primary"
                size="base"
                onClick={() => void handleSubmit()}
              >
                Create Shop
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="h-full overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <OnboardingDesignLibraryContent
          ref={onboardingDesignLibraryContentRef}
          projectId={projectId}
        />
      </div>
    </Modal>
  );
}
